/**
 * Letter-size "Formulario de Registro", prefilled. Same letterhead as the official receipt
 * (olive sidebar, shield, 80 años mark, dashed footer with QR and contact strip).
 * Event name/city/date come from the event — nothing hardcoded per edition.
 * Rendered at 8.5in x 11in (always one page); callers scale it down on narrow screens.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { ReceiptQrCode } from '@/features/receipts/components/ReceiptQrCode';
import { fitScale } from '@/features/events/print/fitScale';
import { VerticalText } from '@/features/receipts/components/VerticalText';
import { DEFAULT_DECLARATION, type PublicRegistration, type RegistrationKind } from '../types';

interface RegistrationFormPrintProps {
  registration: PublicRegistration;
  qrUrl: string;
  /** Drawn signature image (object URL); printed over the signature line */
  signatureUrl?: string | null;
}

const OLIVE = '#8a8a70';
const OLIVE_DARK = '#5f5f4a';

const KIND_LABELS: Record<RegistrationKind, string> = {
  MINISTER: 'MINISTRO',
  DELEGATE: 'DELEGADO',
  GUEST: 'INVITADO',
};

function eventDate(iso: string) {
  return new Date(`${iso}T12:00:00`);
}

function eventSubtitle(reg: PublicRegistration) {
  const monthYear = eventDate(reg.event.start_date)
    .toLocaleDateString('es-BO', { month: 'long', year: 'numeric' })
    .toUpperCase();
  return [reg.event.location?.toUpperCase(), monthYear].filter(Boolean).join(', ');
}

/** Label above a bold value on a writing line (stays visible when the value is empty) */
function Field({ label, value, className }: { label: string; value?: string | null; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[8.5px] font-semibold uppercase tracking-wide" style={{ color: OLIVE_DARK }}>
        {label}
      </p>
      <p className="min-h-[1.55em] border-b border-dotted border-gray-500 text-[12.5px] font-semibold leading-snug">
        {value || ''}
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-3 rounded border border-gray-400">
      <h2
        className="border-b border-gray-400 px-3 py-1 text-[9.5px] font-bold uppercase tracking-[0.12em]"
        style={{ color: OLIVE_DARK, backgroundColor: '#f1f1ea' }}
      >
        {title}
      </h2>
      <div className="grid grid-cols-6 gap-x-5 gap-y-2.5 px-3 pb-3 pt-2">{children}</div>
    </section>
  );
}

export function RegistrationFormPrint({ registration: reg, qrUrl, signatureUrl }: RegistrationFormPrintProps) {
  // Always one sheet: if the content (long address / declaration) is taller than the space above
  // the footer, shrink it instead of letting the footer spill onto a second page.
  const fitRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const fit = fitRef.current;
    const body = bodyRef.current;
    if (!fit || !body) return;
    const measure = () => setScale(fitScale(body.offsetHeight, fit.clientHeight));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(fit);
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  const f = reg.form;
  const isFull = reg.event.registration_form_type === 'FULL_CONVENTION';
  const isDelegate = reg.kind === 'DELEGATE';
  const isMinister = reg.kind === 'MINISTER';
  const isRejected = reg.registration_status === 'REJECTED';
  const signedDigitally = reg.event.signature_mode === 'DIGITAL' && reg.form_signed_at;
  // The form is dated on the event's first day, not when it happened to be printed
  const dateValue = eventDate(reg.event.start_date).toLocaleDateString('es-BO');

  return (
    <div
      className="relative flex overflow-hidden bg-white text-black"
      style={{
        width: '8.5in',
        height: '10.96in',
        fontFamily: 'Arial, Helvetica, sans-serif',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      {isRejected && (
        <div aria-hidden className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <span
            className="select-none font-bold tracking-widest text-rose-700/20"
            style={{ transform: 'rotate(-30deg)', fontSize: '110px' }}
          >
            RECHAZADO
          </span>
        </div>
      )}

      {/* White margin, then the olive sidebar with the vertical title */}
      <div className="shrink-0" style={{ width: '0.35in' }} />
      <div className="flex shrink-0 flex-col" style={{ width: '0.42in' }}>
        <div className="w-full" style={{ height: '1.2in', backgroundColor: OLIVE }} />
        <div style={{ height: '0.15in' }} />
        <VerticalText
          width="100%"
          height="4.4in"
          className="font-light tracking-wide"
          style={{ fontSize: '25px', color: OLIVE }}
        >
          FORMULARIO DE REGISTRO
        </VerticalText>
        <div style={{ height: '0.15in' }} />
        <div className="w-full flex-1" style={{ backgroundColor: OLIVE }} />
      </div>

      {/* Faint "80 años" watermark */}
      <img
        src="/receipt/logo-80.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 select-none"
        style={{ width: '5.5in', transform: 'translate(-45%, -50%)', opacity: 0.07, filter: 'grayscale(1) sepia(0.4)' }}
      />

      <div className="relative z-[1] flex flex-1 flex-col" style={{ padding: '0.45in 0.5in 0.35in' }}>
        <div ref={fitRef} className="min-h-0 flex-1 overflow-hidden">
          <div ref={bodyRef} style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}>
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img src="/receipt/shield.png" alt="Las Asambleas de Dios de Bolivia" style={{ width: '0.95in', height: '0.95in' }} />
            <div>
              <p className="text-xs font-bold uppercase tracking-wide">Las Asambleas de Dios de Bolivia</p>
              <p className="text-[10px] text-gray-600">Construyendo el futuro, honrando la historia</p>
              <p className="text-[10px] text-gray-600">R.S. 101452/61 - 215651/95</p>
            </div>
          </div>
          <img src="/receipt/logo-80.png" alt="80 años" style={{ width: '1.1in' }} />
        </div>

        {/* Title */}
        <div className="mt-4 text-center">
          <h1 className="text-2xl font-bold uppercase tracking-wide">Formulario de Registro</h1>
          <p className="mt-1 text-[13px] font-bold uppercase leading-snug">{reg.event.name}</p>
          <p className="text-[11px] font-semibold text-gray-600">{eventSubtitle(reg)}</p>
        </div>

        {/* Participant kind: strong banner for delegates and guests, quiet pill for ministers */}
        {isMinister ? (
          <div className="mt-2 flex justify-center">
            <span
              className="rounded-full border px-3 py-0.5 text-[10px] font-bold tracking-[0.18em]"
              style={{ borderColor: OLIVE, color: OLIVE_DARK }}
            >
              {KIND_LABELS.MINISTER}
            </span>
          </div>
        ) : (
          <div
            className="mt-3 flex items-center justify-between rounded px-4 py-1.5 text-white"
            style={{ backgroundColor: OLIVE_DARK }}
          >
            <span className="text-base font-bold tracking-[0.3em]">{KIND_LABELS[reg.kind]}</span>
            {isDelegate && (
              <span className="text-[10.5px] font-semibold">
                Participa en representación de {f.represented_name || 'otro ministro / institución'}
              </span>
            )}
          </div>
        )}

        {/* Personal data */}
        <Section title="Datos personales">
          <Field label="Nombre completo" value={f.full_name} className="col-span-3" />
          <Field label="Cédula de identidad" value={f.dni} className="col-span-2" />
        </Section>

        {/* Ministerial data / who the delegate represents / guest origin */}
        {isDelegate ? (
          <Section title="Representa a">
            <Field label="Ministro representado" value={f.represented_name} className="col-span-4" />
            <Field label="Registro ADB" value={f.represented_adb_code} className="col-span-2" />
            <Field label="Iglesia o institución" value={f.institution_note} className="col-span-4" />
            <Field label="C.I. del representado" value={f.represented_ci} className="col-span-2" />
            <p className="col-span-6 text-[9.5px] italic text-gray-600">
              Carta de delegación adjunta digitalmente para fines de validación.
            </p>
          </Section>
        ) : isMinister ? (
          <Section title="Información ministerial">
            <Field label="Distrito" value={f.district_name} className="col-span-3" />
            <Field label="Región" value={f.region_name} className="col-span-3" />
            <Field label="Registro ADB (Nº de ministro)" value={f.adb_code} className={isFull ? 'col-span-2' : 'col-span-6'} />
            {isFull && <Field label="Grado ministerial" value={f.degree_name} className="col-span-2" />}
            {isFull && <Field label="Cargo" value={f.position} className="col-span-2" />}
          </Section>
        ) : (
          <Section title="Procedencia">
            <Field label="Distrito" value={f.district_name} className="col-span-3" />
            <Field label="Región" value={f.region_name} className="col-span-3" />
            <Field label="Iglesia o institución" value={f.institution_note} className="col-span-6" />
          </Section>
        )}

        {/* Contact */}
        <Section title="Contacto">
          <Field label="Celular" value={f.cell_phone} className={isFull ? 'col-span-2' : 'col-span-3'} />
          {isFull && <Field label="Teléfono de oficina" value={f.office_phone} className="col-span-2" />}
          <Field label="Correo electrónico" value={f.email} className={isFull ? 'col-span-2' : 'col-span-3'} />
          {isFull && <Field label="Dirección para envío de correspondencia" value={f.address} className="col-span-6" />}
        </Section>

        {/* Declaration */}
        <p className="mt-3 rounded border border-gray-300 px-3 py-2 text-[11px] font-semibold italic leading-snug">
          {reg.event.registration_declaration || DEFAULT_DECLARATION}
        </p>

        {/* Date and signature */}
        <div className="mt-3 grid grid-cols-2 items-end gap-8">
          <Field label="Fecha" value={dateValue} />
          <div>
            <div className="relative flex h-20 items-end border-b border-black px-1 pb-0.5 text-[9px] text-gray-600">
              {signatureUrl && (
                <img
                  src={signatureUrl}
                  alt="Firma"
                  className="absolute inset-x-0 bottom-1 mx-auto h-[4.4rem] max-w-full object-contain"
                />
              )}
              <span className="relative">{signedDigitally && `Firmado digitalmente ${new Date(reg.form_signed_at as string).toLocaleString('es-BO')}`}</span>
            </div>
            <p className="mt-0.5 text-center text-[8.5px] font-semibold uppercase tracking-wide" style={{ color: OLIVE_DARK }}>
              Firma
            </p>
            <p className="text-center text-[11px] font-semibold leading-tight">{f.full_name}</p>
            <p className="text-center text-[10px] leading-tight text-gray-600">C.I. {f.dni}</p>
          </div>
        </div>

          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 pt-3">
          <div className="border-t border-dashed border-gray-400 pt-2">
            <div className="flex items-end justify-between gap-4">
              <p className="max-w-md text-[9.5px] text-gray-500">
                Código QR de este registro: preséntalo al ingresar al evento. Cédula de identidad adjunta digitalmente
                para fines de validación.
              </p>
              <ReceiptQrCode url={qrUrl} size={72} />
            </div>
            <img
              src="/receipt/redes.png"
              alt="Calle Ballivián N° 1497 · addbolivia@yahoo.es · 73510370 · @ADBolOficial"
              className="mx-auto mt-1"
              style={{ width: '4.2in' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
